import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSetSelectedAccount } from '../context/SidebarContext';
import { useAccounts } from '../context/AccountsContext';
import {
  isAccountOwner,
  hasChatAssistantAccess,
  hasAnyMassMessagesAccess,
  hasPriorityMassMessagesAccess,
  hasAutomatedMessagesSetupAccess,
  hasTokenAccess,
  hasStatsAccess,
} from '../permissions';
import { api } from '../api/client';
import type { Account, Model } from '../api/client';
import { AccountHeader } from '../components/AccountHeader';
import { normalizePlatform } from '../components/PlatformLogo';
import { FeatherIcon } from '../components/FeatherIcon';
import { AccountViewSkeleton } from '../components/LoadingSkeletons';
import { PageTransition } from '../components/PageTransition';
import { Button } from '@heroui/react';
import { ChatAssistantTab } from '../components/ChatAssistant/ChatAssistantTab';
import { MassMessagesTab } from '../components/MassMessages/MassMessagesTab';
import { FanslyMassMessagesTab } from '../components/MassMessages/FanslyMassMessagesTab';
import { PriorityMassMessagesTab } from '../components/PriorityMassMessages/PriorityMassMessagesTab';
import { AutomatedMessagesTab } from '../components/AutomatedMessages/AutomatedMessagesTab';
import { AccountAccessPanel } from '../components/AccountAccess/AccountAccessPanel';
import { isMassMessagesTabVisible } from './accountViewTabs';
import {
  defaultAccountWorkspaceTab,
  isAccountWorkspaceTabVisible,
  normalizeAccountWorkspaceTab,
} from './accountWorkspaceRouting';
import { AccountStatsTab } from '../components/Statistics/AccountStatsTab';

export function AccountViewPage() {
  const { t } = useTranslation('dashboard');
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getPermissions, loadAccountPermissions, isSuperAdmin } = useAuth();
  const setSelectedAccount = useSetSelectedAccount();
  const { updateAccount, subscriptionMap } = useAccounts();
  const subStatus = (accountId && subscriptionMap[accountId]) || null;
  const [account, setAccount] = useState<Account | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestedTab = searchParams.get('tab') || defaultAccountWorkspaceTab(account?.platform);
  const activeTab = normalizeAccountWorkspaceTab(account?.platform, requestedTab);

  useEffect(() => {
    if (!accountId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch account and permissions in parallel
        const [accountData] = await Promise.all([
          api.getAccount(accountId),
          loadAccountPermissions(accountId)
        ]);
        
        setAccount(accountData);
        setSelectedAccount(accountData);
        // Opening an account is a free refresh of its sidebar row.
        updateAccount(accountData.account_id, accountData);
        
        if (accountData.model_id) {
          try {
            api.getModel(accountData.model_id).then(setModel);
          } catch { /* model fetch is best-effort */ }
        }
      } catch {
        setError(t('accountView.notFoundOrNoAccess'));
        setAccount(null);
        setSelectedAccount(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => { setSelectedAccount(null); };
  }, [accountId]);

  // Changes saved inside the account (AI on/off, persona, …) also reach its sidebar row.
  const onAccountUpdate = (updated: Account) => {
    setAccount(updated);
    updateAccount(updated.account_id, updated);
  };

  useEffect(() => {
    if (!account || requestedTab === activeTab) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      return next;
    }, { replace: true });
  }, [account, activeTab, requestedTab, setSearchParams]);

  if (!accountId) return <Navigate to="/dashboard" replace />;

  if (loading) {
    return (
      <AccountViewSkeleton />
    );
  }

  if (error || !account) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-64 text-red-500 gap-4">
        <FeatherIcon name="alert-circle" size={48} />
        <p>{error || t('accountView.accountNotFound')}</p>
        <Button variant="ghost" onPress={() => navigate('/dashboard')}>
          {t('accountView.backToDashboard')}
        </Button>
      </div>
    );
  }

  const perms = getPermissions(accountId);
  const ownerMode = isAccountOwner(perms);
  const showSettings = ownerMode || hasTokenAccess(perms);
  // Offer the guided AI setup only while it has never been completed for this
  // account. `ai_setup_completed_at` is a dedicated marker stamped by the wizard
  // on finish; a migration backfills all pre-existing accounts as done, so the
  // entry only appears on genuinely new accounts. (We can't reuse ai_base_regime:
  // the API pops all ai_* columns before write, so it's always null on read.)
  const needsGuidedSetup = hasChatAssistantAccess(perms) && !account.ai_setup_completed_at;

  // Automated messages ARE supported on Fansly (follow-welcome flow in bigdayz.new).
  const isFansly = normalizePlatform(account.platform) === 'fansly';

  const tabs: { id: string; label: string; icon: string; visible: boolean }[] = [
    { id: 'chat-assistant', label: t('accountView.tabs.chatAssistant'), icon: 'message-circle', visible: isAccountWorkspaceTabVisible(account.platform, 'chat-assistant', hasChatAssistantAccess(perms)) },
    { id: 'mass-messages', label: t('accountView.tabs.massMessages'), icon: 'send', visible: isAccountWorkspaceTabVisible(account.platform, 'mass-messages', isMassMessagesTabVisible(isFansly ? 'fansly' : 'onlyfans', hasAnyMassMessagesAccess(perms))) },
    { id: 'priority-mass-messages', label: t('accountView.tabs.priorityMassMessages'), icon: 'zap', visible: isAccountWorkspaceTabVisible(account.platform, 'priority-mass-messages', hasPriorityMassMessagesAccess(perms)) },
    { id: 'automated-messages', label: t('accountView.tabs.automatedMessages'), icon: 'clock', visible: isAccountWorkspaceTabVisible(account.platform, 'automated-messages', hasAutomatedMessagesSetupAccess(perms)) },
    // Access is NOT an underline tab: it opens from the "users" button beside the settings gear.
    { id: 'stats', label: t('accountView.tabs.stats'), icon: 'bar-chart-2', visible: isAccountWorkspaceTabVisible(account.platform, 'stats', ownerMode || hasStatsAccess(perms) || isSuperAdmin) },
  ];

  const visibleTabs = tabs.filter((t) => t.visible);

  const modelName = model?.model_name || null;
  const openTab = (tab: string) =>
    setSearchParams((prev) => ({
      ...Object.fromEntries(prev.entries()),
      tab,
    }));

  return (
    <PageTransition>
      <div className="mobile-page-shell p-6 min-w-0">
      <AccountHeader
        account={account}
        modelName={modelName}
        subStatus={subStatus}
        tabs={visibleTabs}
        activeTab={activeTab}
        onTab={openTab}
        onSettings={showSettings ? () => navigate(`/account/${accountId}/settings`) : undefined}
        onAccess={ownerMode ? () => openTab('access') : undefined}
        onFinishSetup={needsGuidedSetup ? () => navigate(`/account/${accountId}/ai-setup`) : undefined}
      />

      {visibleTabs.length > 0 ? (
        <>
          {activeTab === 'chat-assistant' && hasChatAssistantAccess(perms) && (
            <ChatAssistantTab account={account} onAccountUpdate={onAccountUpdate} />
          )}
          {activeTab === 'mass-messages' && hasAnyMassMessagesAccess(perms) && (
            isFansly
              ? <FanslyMassMessagesTab accountId={accountId} />
              : <MassMessagesTab accountId={accountId} />
          )}
          {activeTab === 'priority-mass-messages' && hasPriorityMassMessagesAccess(perms) && (
            <PriorityMassMessagesTab accountId={accountId} platform={isFansly ? 'fansly' : 'onlyfans'} />
          )}
          {activeTab === 'automated-messages' && hasAutomatedMessagesSetupAccess(perms) && (
            <AutomatedMessagesTab accountId={accountId} />
          )}
          {activeTab === 'stats' && (ownerMode || hasStatsAccess(perms) || isSuperAdmin) && (
            <AccountStatsTab account={account} />
          )}
          {activeTab === 'access' && ownerMode && <AccountAccessPanel accountId={accountId} />}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-40 text-[var(--color-text-tertiary)]">
          <FeatherIcon name="lock" size={32} className="mb-2 opacity-50" />
          <p>{t('accountView.noFeatureAccess')}</p>
        </div>
      )}
      </div>
    </PageTransition>
  );
}
